import { useFormContext } from 'react-hook-form';
import { BaseCheckbox, Card, ControlledTextField } from '../../../../shared/components';
import ClientContactSection from '../components/ClientContactSection';
import OwnerContactSection from '../components/OwnerContactSection';
import POCContactSection from '../components/POCContactSection';
import { useSameAsCopy } from '../hooks/useSameAsCopy';

/**
 * "Same as X" copies the contact details only.
 *
 * Each section points at a different `/api/poc/*` master, so carrying the source's `id`
 * across would make the job reference a row in the wrong table — the copy is always a
 * new contact for the target's directory. Section-specific fields (the owner's building
 * and supervisor, the client's company and work order) are left untouched.
 */
const CONTACT_KEYS = ['name', 'phone', 'mobile', 'whatsapp', 'email', 'officeAddress'];

function contactCopy(current = {}, source = {}) {
  const copied = Object.fromEntries(CONTACT_KEYS.map((key) => [key, source[key] ?? '']));
  return { ...current, ...copied, id: null };
}

export default function StepPOC() {
  const { watch, setValue, getValues } = useFormContext();

  const architect = watch('architect');
  const builder = watch('builder');
  const owner = watch('owner');

  const architectDeps = [
    watch('architect.name'),
    watch('architect.phone'),
    watch('architect.mobile'),
    watch('architect.whatsapp'),
    watch('architect.email'),
    watch('architect.officeAddress'),
  ];

  const builderDeps = [
    watch('builder.name'),
    watch('builder.phone'),
    watch('builder.mobile'),
    watch('builder.whatsapp'),
    watch('builder.email'),
    watch('builder.officeAddress'),
  ];

  const ownerDeps = [
    watch('owner.name'),
    watch('owner.phone'),
    watch('owner.mobile'),
    watch('owner.whatsapp'),
    watch('owner.email'),
  ];

  const clientDeps = [
    watch('client.name'),
    watch('client.phone'),
    watch('client.mobile'),
    watch('client.whatsapp'),
    watch('client.email'),
    watch('client.officeAddress'),
  ];

  // ---- Builder ← Architect ----
  const builderCopy = useSameAsCopy({
    targetDeps: builderDeps,
    sources: { ARCHITECT: architect },
    sourceDepsMap: {
      ARCHITECT: architectDeps,
    },
    onCopy: (v) =>
      setValue('builder', contactCopy(getValues('builder'), v), {
        shouldDirty: true,
        shouldValidate: false,
      }),
  });

  // ---- Owner ← Architect / Builder ----
  const ownerCopy = useSameAsCopy({
    targetDeps: ownerDeps,
    sources: {
      ARCHITECT: architect,
      BUILDER: builder,
    },
    sourceDepsMap: {
      ARCHITECT: architectDeps,
      BUILDER: builderDeps,
    },
    onCopy: (v) =>
      setValue('owner', contactCopy(getValues('owner'), v), {
        shouldDirty: true,
        shouldValidate: false,
      }),
  });

  const clientCopy = useSameAsCopy({
    targetDeps: clientDeps,
    sources: {
      ARCHITECT: architect,
      BUILDER: builder,
      OWNER: owner,
    },
    sourceDepsMap: {
      ARCHITECT: architectDeps,
      BUILDER: builderDeps,
      OWNER: ownerDeps,
    },
    onCopy: (v) =>
      setValue('client', contactCopy(getValues('client'), v), {
        shouldDirty: true,
        shouldValidate: false,
      }),
  });

  return (
    <Card title='Point of Communication'>
      <div className='column gap-4'>
        <POCContactSection title='1. Architect Details' path='architect' />

        <POCContactSection
          title='2. Builder/Developer Details'
          path='builder'
          headerActions={
            <BaseCheckbox
              id='builder-same-as-architect'
              checked={builderCopy.activeSource === 'ARCHITECT'}
              onChange={(v) => (v ? builderCopy.copyFrom('ARCHITECT') : builderCopy.unlink())}
              label='Same as Architect'
            />
          }
        />

        <OwnerContactSection
          headerActions={
            <div className='row gap-3'>
              <BaseCheckbox
                checked={ownerCopy.activeSource === 'ARCHITECT'}
                onChange={(v) => (v ? ownerCopy.copyFrom('ARCHITECT') : ownerCopy.unlink())}
                label='Same as Architect'
              />
              <BaseCheckbox
                checked={ownerCopy.activeSource === 'BUILDER'}
                onChange={(v) => (v ? ownerCopy.copyFrom('BUILDER') : ownerCopy.unlink())}
                label='Same as Builder/Developer'
              />
            </div>
          }
        />

        <ClientContactSection
          headerActions={
            <div className='row gap-3'>
              <BaseCheckbox
                checked={clientCopy.activeSource === 'ARCHITECT'}
                onChange={(v) => (v ? clientCopy.copyFrom('ARCHITECT') : clientCopy.unlink())}
                label='Same as Architect'
              />
              <BaseCheckbox
                checked={clientCopy.activeSource === 'BUILDER'}
                onChange={(v) => (v ? clientCopy.copyFrom('BUILDER') : clientCopy.unlink())}
                label='Same as Builder/Developer'
              />
              <BaseCheckbox
                checked={clientCopy.activeSource === 'OWNER'}
                onChange={(v) => (v ? clientCopy.copyFrom('OWNER') : clientCopy.unlink())}
                label='Same as Owner'
              />
            </div>
          }
        />
      </div>

      <ControlledTextField
        id='pocNotes'
        name='pocNotes'
        label='5. Notes'
        placeholder='Add any internal notes for this step (optional)'
        isMultiline
        rows={4}
        rules={{
          maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' },
        }}
        showCharCount
      />
    </Card>
  );
}
